/**
 * Shipment weight rules — one place, so the form and the box rows agree.
 *
 * Per box, the chargeable basis is the HIGHER of actual and volumetric weight
 * (standard courier practice). Then:
 *
 *  Vendor chargeable — each box rounded UP to the next 0.5 kg, summed, and the
 *                      total rounded to a whole kg.
 *                      26.3 + 27.6  ->  26.5 + 28  ->  54.5  ->  55
 *
 *  Capex chargeable  — each box rounded UP to the next whole kg, summed.
 *                      26.3 + 27.6  ->  27 + 28  ->  55
 */
export const VOLUMETRIC_DIVISOR = 5000;

// Guards against float noise (e.g. 26.5 * 2 = 53.000000000000004 -> 54).
const EPS = 1e-9;

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

/** L x B x H / 5000. Boxes store height in `width` (legacy naming). */
export const boxVolumetric = (box) =>
    (num(box.length) * num(box.breadth) * num(box.width ?? box.height)) / VOLUMETRIC_DIVISOR;

export const boxActual = (box) => num(box.weight);

/** Weight a box is charged on: max(actual, volumetric). */
export const boxChargeBasis = (box) => Math.max(boxActual(box), boxVolumetric(box));

/** Up to the next 0.5 kg. */
export const vendorBoxWeight = (box) => {
    const w = boxChargeBasis(box);
    return w > 0 ? Math.ceil(w * 2 - EPS) / 2 : 0;
};

/** Up to the next whole kg. */
export const capexBoxWeight = (box) => {
    const w = boxChargeBasis(box);
    return w > 0 ? Math.ceil(w - EPS) : 0;
};

const round2 = (n) => Math.round(n * 100) / 100;

/** Totals for the Service Details card. */
export const shipmentWeights = (boxes = []) => {
    const actual = boxes.reduce((s, b) => s + boxActual(b), 0);
    const volumetric = boxes.reduce((s, b) => s + boxVolumetric(b), 0);
    const vendorSum = boxes.reduce((s, b) => s + vendorBoxWeight(b), 0);
    return {
        actual: round2(actual),
        volumetric: round2(volumetric),
        charged: round2(Math.max(actual, volumetric)),
        // Box values are multiples of 0.5, so the total is x or x.5 and
        // Math.round takes x.5 up to the next kg.
        vendorChargeable: Math.round(vendorSum + EPS),
        capexChargeable: boxes.reduce((s, b) => s + capexBoxWeight(b), 0),
    };
};

/** Sum of rate x quantity over every line item in every box. */
export const shipmentValue = (boxes = []) =>
    round2(
        boxes.reduce(
            (s, b) => s + (b.items || []).reduce((t, it) => t + (parseFloat(it.rate) || 0) * (parseFloat(it.quantity) || 0), 0),
            0
        )
    );
